import React, { Suspense, useEffect, useState } from "react";
import TemplateLoader, {
  TemplateDefinition,
} from "../../infrastructure/template-loader";
import { ApplicationDesignSettings } from "../../infrastructure/application-design-service";
import ServiceLocator from "../../domain/service-locator";

interface TemplateAwareAppProps {
  applicationId: string;
  fallbackTemplate?: string;
}

const TemplateAwareApp: React.FC<TemplateAwareAppProps> = ({
  applicationId,
  fallbackTemplate = "general",
}) => {
  const [template, setTemplate] = useState<TemplateDefinition | null>(null);
  const [designSettings, setDesignSettings] =
    useState<ApplicationDesignSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadTemplate = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // ServiceLocatorから既に設定されたテンプレート情報を取得
        const uiTemplateModule = ServiceLocator.UiTemplateModule;
        const templateConfig = uiTemplateModule.templateConfiguration;

        // テンプレートキーの決定
        let templateKey = fallbackTemplate;
        if (templateConfig.templateKey) {
          templateKey = templateConfig.templateKey;
        }

        // デザイン設定の構築
        const settings: ApplicationDesignSettings = {
          applicationId,
          designTemplateKey: templateKey,
          templateConfiguration: templateConfig.templateConfiguration,
          customDesignConfig: templateConfig.customDesignConfig,
        };
        setDesignSettings(settings);

        // テンプレートの取得
        const selectedTemplate = TemplateLoader.getTemplate(templateKey);

        if (selectedTemplate) {
          setTemplate(selectedTemplate);
        } else {
          // フォールバックテンプレートを使用
          const fallbackTemplateDefinition =
            TemplateLoader.getTemplate(fallbackTemplate);
          setTemplate(
            fallbackTemplateDefinition || TemplateLoader.getDefaultTemplate()
          );
          setError(
            `Template '${templateKey}' not found, using fallback template`
          );
        }
      } catch (err) {
        console.error("Failed to load template:", err);
        setError("Failed to load template");
        // エラーの場合もデフォルトテンプレートを使用
        const defaultTemplate =
          TemplateLoader.getTemplate(fallbackTemplate) ||
          TemplateLoader.getDefaultTemplate();
        setTemplate(defaultTemplate);
      } finally {
        setIsLoading(false);
      }
    };

    loadTemplate();
  }, [applicationId, fallbackTemplate]);

  if (isLoading) {
    return <div>Loading application...</div>;
  }

  if (!template) {
    return <div>Failed to load template</div>;
  }

  const TemplateComponent = template.component;

  return (
    <Suspense fallback={<></>}>
      <TemplateComponent
        applicationId={applicationId}
        designSettings={designSettings}
        error={error}
      />
    </Suspense>
  );
};

export default TemplateAwareApp;
